import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon, EyeIcon } from '@heroicons/react/24/outline';

const OnboardingWaivers = ({ userId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/onboarding-waivers/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then(setRows)
      .catch(() => setRows([]));
  }, [userId]);
  if (!rows) return null;
  return (
    <div className="border-b pb-4" data-testid="hr-onboarding-waivers">
      <h3 className="font-semibold mb-2">Company Waivers (signed during onboarding)</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No waiver is flagged "new hires must sign" yet — flag one in Company Hub → Waivers.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((w) => (
            <div key={w.id} className="flex items-center justify-between p-3 bg-gray-50 rounded" data-testid={`hr-waiver-${w.id}`}>
              <div className="flex items-center">
                {w.signed ? <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" /> : <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />}
                <span className={`text-sm ${w.signed ? 'text-gray-900' : 'text-gray-500'}`}>{w.title}{w.signed_at ? <span className="text-xs text-slate-500"> · signed {new Date(w.signed_at).toLocaleDateString()}</span> : <span className="text-xs text-red-500"> · not signed</span>}</span>
              </div>
              {w.drive_link && <a href={w.drive_link} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">View signed PDF</a>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OnboardingTicketFiling = ({ userId, hideWhenEmpty = false, title = null }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem('token');
  const load = React.useCallback(() => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/onboarding-tickets/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => (r.ok ? r.json() : null)).then(setInfo).catch(() => setInfo(null)), [userId, token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!info?.files?.some((f) => f.drive_status === 'pending' || f.drive_status === 'filing')) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [info, load]);
  const refile = async (force) => {
    setBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/onboarding-tickets/${userId}/refile`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ force }) });
      const d = await r.json();
      alert(r.ok ? `Filing ${d.queued} ticket photo(s) into the Safety folder — this takes about a minute each.` : (d.detail || 'Failed'));
      setTimeout(load, 1500);
    } finally { setBusy(false); }
  };
  if (!info) return null;
  const done = info.files.filter((f) => f.drive_status === 'done').length;
  const unfiled = info.legacy_unfiled + info.files.filter((f) => f.drive_status !== 'done').length;
  if (hideWhenEmpty && info.files.length === 0 && info.legacy_unfiled === 0) return null;
  return (
    <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm" data-testid="hr-ticket-filing">
      {title && <p className="text-xs font-medium text-slate-600 mb-1">{title}</p>}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span><b>Filed to Safety folder:</b> {done} · <b>Ticket records in Ops:</b> {info.ticket_records}{unfiled > 0 && <span className="text-red-600"> · {unfiled} not filed yet</span>}</span>
        <div className="flex gap-2">
          {unfiled > 0 && <button disabled={busy} onClick={() => refile(false)} className="px-3 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700 disabled:opacity-50" data-testid="hr-ticket-refile">File to Safety now</button>}
          <button disabled={busy} onClick={() => { if (window.confirm('Re-file every onboarding ticket photo into the Safety folder and rebuild their ticket records?')) refile(true); }} className="px-3 py-1 bg-white border border-gray-300 text-xs rounded hover:bg-gray-100 disabled:opacity-50" data-testid="hr-ticket-refile-all">Re-file all</button>
        </div>
      </div>
      {info.files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {info.files.map((f) => (
            <li key={f.id} className="flex items-center gap-2 text-xs" data-testid={`hr-ticket-file-${f.id}`}>
              <span className={`h-2 w-2 rounded-full ${f.drive_status === 'done' ? 'bg-green-500' : f.drive_status === 'error' ? 'bg-red-500' : 'bg-blue-400 animate-pulse'}`} />
              <span className="text-gray-800">{f.drive_status === 'done' ? (f.ticket_name || 'Filed') : f.filename}</span>
              {f.expiry_date && <span className="text-slate-500">exp {f.expiry_date}</span>}
              {f.drive_link && <a href={f.drive_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Drive</a>}
              {f.drive_status === 'error' && <span className="text-red-500">{f.drive_error}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const HROnboardingReview = () => {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPendingReviews();
  }, []);

  const fetchPendingReviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/hr/pending-onboarding`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      console.log('Pending onboarding data:', data);
      setPendingReviews(data.pending_users || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch pending reviews:', error);
      setLoading(false);
    }
  };

  const handleReview = async (userId, approved) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('approved', approved);
      if (!approved) {
        formData.append('rejection_reason', rejectionReason);
      }

      const response = await fetch(`${backendUrl}/api/onboarding/review/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        alert(`Onboarding ${approved ? 'approved' : 'rejected'} successfully`);
        setSelectedUser(null);
        setRejectionReason('');
        fetchPendingReviews();
      } else {
        alert('Failed to process review');
      }
    } catch (error) {
      console.error('Failed to process review:', error);
      alert('Failed to process review');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (selectedUser) {
    const user = pendingReviews.find(u => u.user_id === selectedUser);
    const data = user?.onboarding_data;

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold">{user.full_name}</h2>
            <p className="text-gray-600">{user.email}</p>
          </div>
          <button
            onClick={() => setSelectedUser(null)}
            className="text-blue-600 hover:text-blue-800"
          >
            ← Back to List
          </button>
        </div>

        <div className="space-y-6">
          {/* Video Status */}
          <div className="border-b pb-4">
            <h3 className="font-semibold mb-2">Safety Video</h3>
            <p className={data.video_watched ? 'text-green-600' : 'text-red-600'}>
              {data.video_watched ? '✓ Completed' : '✗ Not completed'}
            </p>
          </div>

          <OnboardingWaivers userId={user.user_id} />

          {/* Onboarding Forms */}
          <div className="border-b pb-4">
            <h3 className="font-semibold mb-2">Onboarding Forms</h3>
            <div className="space-y-3">
              {[
                { key: 'application_info_completed', label: 'Application Info', pdfKey: 'application_info_pdf' },
                { key: 'cert_check_completed', label: 'Cert Check', pdfKey: 'cert_check_pdf' },
                { key: 'health_card_uploaded', label: 'Health Card', pdfKey: 'health_card_path' },
                { key: 'drivers_license_uploaded', label: "Driver's License", pdfKey: 'drivers_license_path' },
                { key: 'direct_deposit_completed', label: 'Direct Deposit', pdfKey: 'direct_deposit_pdf' },
                { key: 'tax_forms_completed', label: 'TD1 Federal', pdfKey: 'tax_forms_pdf' },
                { key: 'tax_forms_completed', label: 'TD1ON Ontario', pdfKey: 'tax_forms_on_pdf' },
                { key: 'acknowledgement_completed', label: 'Acknowledgement', pdfKey: 'acknowledgement_pdf' },
                { key: 'insurance_consent_completed', label: 'Insurance Consent', pdfKey: 'insurance_consent_pdf' }
              ].map(field => (
                <div key={field.label} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex items-center">
                    {data[field.key] ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                    ) : (
                      <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                    )}
                    <span className={`text-sm ${data[field.key] ? 'text-gray-900' : 'text-gray-500'}`}>
                      {field.label}
                    </span>
                  </div>
                  {data[field.pdfKey] && (
                    <a
                      href={data[field.pdfKey]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                    >
                      View PDF
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Void cheque (only relevant if Direct Deposit was skipped) */}
          {data.direct_deposit_skipped && (
            <div className="border-b pb-4" data-testid="hr-void-cheque-status">
              <h3 className="font-semibold mb-2">Direct Deposit Slip (Void Cheque)</h3>
              {data.direct_deposit_voidcheque_uploaded ? (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded">
                  <span className="flex items-center text-sm text-gray-900">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                    Void cheque uploaded
                  </span>
                  {data.direct_deposit_voidcheque_pdf && (
                    <a href={data.direct_deposit_voidcheque_pdf} target="_blank" rel="noopener noreferrer"
                       className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">View</a>
                  )}
                </div>
              ) : (
                <div className="flex items-center p-3 bg-amber-50 border border-amber-300 rounded text-sm text-amber-900">
                  <XCircleIcon className="h-5 w-5 text-amber-600 mr-2" />
                  Void cheque pending — employee skipped Direct Deposit and hasn't uploaded a slip yet.
                </div>
              )}
            </div>
          )}

          {/* Safety Tickets */}
          {data.safety_tickets?.length > 0 && (
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-2">Safety Tickets ({data.safety_tickets.length})</h3>
              <div className="space-y-2">
                {data.safety_tickets.map((ticket, index) => (
                  <a
                    key={index}
                    href={`${backendUrl}${ticket}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm block"
                  >
                    View Ticket {index + 1}
                  </a>
                ))}
              </div>
              <OnboardingTicketFiling userId={user.user_id} />
            </div>
          )}

          {/* Review Actions */}
          <div className="pt-4">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Rejection Reason (if rejecting)</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg"
                rows="3"
                placeholder="Provide reason if rejecting..."
              />
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => handleReview(user.user_id, true)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center"
              >
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Approve
              </button>
              <button
                onClick={() => handleReview(user.user_id, false)}
                disabled={!rejectionReason}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircleIcon className="h-5 w-5 mr-2" />
                Reject
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b">
        <h2 className="text-2xl font-bold">Pending Onboarding Reviews</h2>
        <p className="text-gray-600">Review and approve employee onboarding submissions</p>
      </div>

      {pendingReviews.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          No pending onboarding reviews
        </div>
      ) : (
        <div className="divide-y">
          {pendingReviews.map(user => (
            <div key={user.user_id} className="p-6 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">{user.full_name}</h3>
                  <p className="text-gray-600 text-sm">{user.email}</p>
                  <p className="text-gray-500 text-xs mt-1">
                    Submitted: {new Date(user.submitted_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedUser(user.user_id)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center"
                >
                  <EyeIcon className="h-5 w-5 mr-2" />
                  Review
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { OnboardingTicketFiling };
export default HROnboardingReview;
